import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

// Specialization qualification: the rules a cohort's learners must meet
// before choosing a specialization, and support tools for one learner
// (enrollment_mngt; docs/api/specialization.md in lms_backend).
const BASE = '/specialization';
const KEY = ['specialization'];
export const LEARNERS_PAGE_SIZE = 50;
const MAX_BULK = 200;

const clean = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

export const useGate = (cohortId) =>
  useQuery(
    [...KEY, 'gate', cohortId],
    async () => (await axiosInstancev2.get(`${BASE}/gates/${cohortId}`)).data,
    { enabled: Boolean(cohortId), refetchOnWindowFocus: false, retry: false }
  );

// Dry run: saves nothing. With impact, every learner is evaluated, so only
// ask for it when confirming.
export const checkGate = async (cohortId, body, { impact = false } = {}) =>
  (
    await axiosInstancev2.post(`${BASE}/gates/${cohortId}/check`, body, {
      params: impact ? { impact: 'true' } : undefined,
    })
  ).data;

export const useSaveGate = (cohortId) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (body) =>
      (await axiosInstancev2.put(`${BASE}/gates/${cohortId}`, body)).data,
    {
      onSuccess: (gate) => {
        queryClient.setQueryData([...KEY, 'gate', cohortId], gate);
        queryClient.invalidateQueries([...KEY, 'learners', cohortId]);
      },
    }
  );
};

export const useLearners = (
  cohortId,
  { status, q },
  offset = 0,
  options = {}
) => {
  const params = clean({
    cohort_id: cohortId,
    status,
    q,
    limit: LEARNERS_PAGE_SIZE,
    offset,
  });
  return useQuery(
    [...KEY, 'learners', cohortId, params],
    async () =>
      (await axiosInstancev2.get(`${BASE}/students`, { params })).data,
    {
      enabled: Boolean(cohortId),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
      ...options,
    }
  );
};

export const useLearner = (cohortId, studentId) =>
  useQuery(
    [...KEY, 'learner', cohortId, studentId],
    async () =>
      (
        await axiosInstancev2.get(`${BASE}/students/${studentId}`, {
          params: { cohort_id: cohortId },
        })
      ).data,
    {
      enabled: Boolean(cohortId && studentId),
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

// Every learner action changes the list, its counts and that learner.
const useLearnerMutation = (cohortId, fn) => {
  const queryClient = useQueryClient();
  return useMutation(fn, {
    onSettled: () => {
      queryClient.invalidateQueries([...KEY, 'learners', cohortId]);
      queryClient.invalidateQueries([...KEY, 'learner', cohortId]);
    },
  });
};

export const useApproval = (cohortId) =>
  useLearnerMutation(cohortId, async ({ studentId, approve }) => {
    const config = { params: { cohort_id: cohortId, student_id: studentId } };
    return (
      approve
        ? await axiosInstancev2.put(`${BASE}/approvals`, null, config)
        : await axiosInstancev2.delete(`${BASE}/approvals`, config)
    ).data;
  });

export const useBulkApprove = (cohortId) =>
  useLearnerMutation(cohortId, async (studentIds) => {
    const result = { approved: [], skipped: [] };
    for (let i = 0; i < studentIds.length; i += MAX_BULK) {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await axiosInstancev2.put(`${BASE}/approvals`, {
        cohort_id: cohortId,
        student_ids: studentIds.slice(i, i + MAX_BULK),
      });
      result.approved.push(...(data?.approved ?? []));
      result.skipped.push(...(data?.skipped ?? []));
    }
    return result;
  });

export const useChooseForLearner = (cohortId) =>
  useLearnerMutation(
    cohortId,
    async ({ studentId, trackId, replaceTrackId, skipQualification }) =>
      (
        await axiosInstancev2.post(
          `${BASE}/selections`,
          clean({
            cohort_id: cohortId,
            student_id: studentId,
            track_id: trackId,
            replace_track_id: replaceTrackId,
            skip_qualification: skipQualification || undefined,
          })
        )
      ).data
  );

export const useResetChoice = (cohortId) =>
  useLearnerMutation(
    cohortId,
    async ({ studentId, trackId }) =>
      (
        await axiosInstancev2.delete(`${BASE}/selections`, {
          params: clean({
            cohort_id: cohortId,
            student_id: studentId,
            track_id: trackId,
          }),
        })
      ).data
  );

export const errorCode = (error) => error?.response?.data?.code;

// The server writes admin-facing messages; show them as they are.
export const specError = (error, fallback) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const message = data?.message || data?.error;
  if (status === 404 && !message) {
    return "Specialization qualification isn't available on the server yet.";
  }
  if (!error?.response) {
    return 'We couldn’t reach the server. Check your connection and try again.';
  }
  return message || fallback;
};
